import { useState, useCallback } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { municipiosPara, municipiosAmapa } from '@/data/municipalities';

const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyl0wmpkn9mLPKfZxhBP-qHWR37jXtAsPZgqEs1qf0x4UZImiWrqqnRD4YBGDT3RdHZ5g/exec';

const comoSoubeOptions = ['Redes Sociais', 'Indicação de amigo(a)', 'Ação Itinerante', 'Rádio/TV', 'Outro'];

function phoneMask(value) {
  const nums = value.replace(/\D/g, '').slice(0, 11);
  if (nums.length <= 2) return `(${nums}`;
  if (nums.length <= 7) return `(${nums.slice(0, 2)}) ${nums.slice(2)}`;
  return `(${nums.slice(0, 2)}) ${nums.slice(2, 7)}-${nums.slice(7)}`;
}

export default function RegistrationForm() {
  const [ref, vis] = useScrollReveal();
  const [form, setForm] = useState({
    nome: '', email: '', telefone: '', estado: '', municipio: '',
    colonia: '', comoSoube: '', mensagem: '', aceite: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const municipios = form.estado === 'PA' ? municipiosPara : form.estado === 'AP' ? municipiosAmapa : [];

  const handleChange = useCallback((field, value) => {
    if (field === 'telefone') value = phoneMask(value);
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'estado') next.municipio = '';
      return next;
    });
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.nome.trim()) e.nome = 'Nome é obrigatório';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido';
    if (!form.telefone || form.telefone.replace(/\D/g, '').length < 10) e.telefone = 'Telefone inválido';
    if (!form.estado) e.estado = 'Selecione o estado';
    if (!form.municipio) e.municipio = 'Selecione o município';
    if (!form.colonia) e.colonia = 'Selecione uma opção';
    if (!form.aceite) e.aceite = 'Você precisa concordar com os termos';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(false);

    const params = new URLSearchParams();
    params.append('nome', form.nome);
    params.append('email', form.email);
    params.append('telefone', form.telefone);
    params.append('estado', form.estado);
    params.append('municipio', form.municipio);
    params.append('colonia', form.colonia);
    params.append('comoSoube', form.comoSoube);
    params.append('mensagem', form.mensagem);
    params.append('timestamp', new Date().toLocaleString('pt-BR'));

    try {
      await fetch(SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSuccess(true);
      setForm({ nome: '', email: '', telefone: '', estado: '', municipio: '', colonia: '', comoSoube: '', mensagem: '', aceite: false });
    } catch {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="cadastro"
      ref={ref}
      data-testid="registration-form-section"
      className={`py-20 sm:py-28 bg-gradient-to-b from-[#f0faf8] to-white transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-[#1A8C7A]/10 text-[#1A8C7A] text-sm font-semibold rounded-full mb-4 uppercase tracking-wide">
            Cadastro
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3">
            FAÇA PARTE DO SINDPESCA
          </h2>
          <p className="text-gray-500 text-lg">
            Cadastre-se e fortaleça a pesca artesanal no Pará e Amapá
          </p>
        </div>

        {success ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg">
            <div className="w-20 h-20 bg-[#1A8C7A]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-[#1A8C7A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Cadastro realizado!</h3>
            <p className="text-gray-500 mb-8">Em breve entraremos em contato.</p>
            <button
              onClick={() => setSuccess(false)}
              data-testid="registration-new"
              className="mt-6 px-6 py-2 text-sm text-[#1A8C7A] border border-[#1A8C7A] rounded-full hover:bg-[#1A8C7A] hover:text-white transition-colors"
            >
              Fazer novo cadastro
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="registration-form"
            className="bg-white rounded-2xl shadow-lg p-8 sm:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

              {/* Nome */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Completo *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => handleChange('nome', e.target.value)}
                  className={`w-full h-12 px-4 rounded-lg border bg-gray-50 text-base focus:outline-none focus:ring-2 focus:ring-[#1A8C7A] focus:border-[#1A8C7A] transition-colors ${errors.nome ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Seu nome completo"
                />
                {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">E-mail *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full h-12 px-4 rounded-lg border bg-gray-50 text-base focus:outline-none focus:ring-2 focus:ring-[#1A8C7A] focus:border-[#1A8C7A] transition-colors ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="seu@email.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Telefone */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Telefone / WhatsApp *</label>
                <input
                  type="tel"
                  value={form.telefone}
                  onChange={(e) => handleChange('telefone', e.target.value)}
                  className={`w-full h-12 px-4 rounded-lg border bg-gray-50 text-base focus:outline-none focus:ring-2 focus:ring-[#1A8C7A] focus:border-[#1A8C7A] transition-colors ${errors.telefone ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="(XX) XXXXX-XXXX"
                />
                {errors.telefone && <p className="text-red-500 text-xs mt-1">{errors.telefone}</p>}
              </div>

              {/* Estado */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Estado *</label>
                <select
                  value={form.estado}
                  onChange={(e) => handleChange('estado', e.target.value)}
                  className={`w-full h-12 px-4 rounded-lg border bg-gray-50 text-base focus:outline-none focus:ring-2 focus:ring-[#1A8C7A] focus:border-[#1A8C7A] transition-colors ${errors.estado ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Selecione o estado</option>
                  <option value="PA">Pará (PA)</option>
                  <option value="AP">Amapá (AP)</option>
                </select>
                {errors.estado && <p className="text-red-500 text-xs mt-1">{errors.estado}</p>}
              </div>

              {/* Município */}
              <div data-testid="municipio-field">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Município / Cidade *</label>
                <select
                  data-testid="form-municipio"
                  value={form.municipio}
                  onChange={(e) => handleChange('municipio', e.target.value)}
                  disabled={!form.estado}
                  className={`w-full h-12 px-4 rounded-lg border bg-gray-50 text-base focus:outline-none focus:ring-2 focus:ring-[#1A8C7A] focus:border-[#1A8C7A] transition-colors disabled:opacity-50 ${errors.municipio ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">{form.estado ? 'Selecione o município' : 'Selecione o estado primeiro'}</option>
                  {municipios.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                {errors.municipio && <p className="text-red-500 text-xs mt-1">{errors.municipio}</p>}
              </div>

              {/* Colônia */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Já é associado(a) a alguma colônia de pesca? *</label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="colonia"
                      value="Sim"
                      checked={form.colonia === 'Sim'}
                      onChange={(e) => handleChange('colonia', e.target.value)}
                      className="w-5 h-5 text-[#1A8C7A] accent-[#1A8C7A]"
                    />
                    <span className="text-gray-700">Sim</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="colonia"
                      value="Não"
                      checked={form.colonia === 'Não'}
                      onChange={(e) => handleChange('colonia', e.target.value)}
                      className="w-5 h-5 text-[#1A8C7A] accent-[#1A8C7A]"
                    />
                    <span className="text-gray-700">Não</span>
                  </label>
                </div>
                {errors.colonia && <p className="text-red-500 text-xs mt-1">{errors.colonia}</p>}
              </div>

              {/* Como soube */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Como ficou sabendo do SINDPESCA?</label>
                <select
                  value={form.comoSoube}
                  onChange={(e) => handleChange('comoSoube', e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border border-gray-300 bg-gray-50 text-base focus:outline-none focus:ring-2 focus:ring-[#1A8C7A] focus:border-[#1A8C7A] transition-colors"
                >
                  <option value="">Selecione</option>
                  {comoSoubeOptions.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>

              {/* Mensagem */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Mensagem / Observações</label>
                <textarea
                  value={form.mensagem}
                  onChange={(e) => handleChange('mensagem', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-50 text-base focus:outline-none focus:ring-2 focus:ring-[#1A8C7A] focus:border-[#1A8C7A] transition-colors resize-none"
                  placeholder="Escreva sua mensagem (opcional)"
                />
              </div>

              {/* Aceite */}
              <div className="sm:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.aceite}
                    onChange={(e) => handleChange('aceite', e.target.checked)}
                    className="mt-1 w-5 h-5 text-[#1A8C7A] accent-[#1A8C7A] rounded"
                  />
                  <span className="text-gray-600 text-sm">
                    Concordo em ter meus dados utilizados pelo SINDPESCA para fins de cadastro e comunicação. *
                  </span>
                </label>
                {errors.aceite && <p className="text-red-500 text-xs mt-1">{errors.aceite}</p>}
              </div>

            </div>

            {submitError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                Ocorreu um erro. Tente novamente mais tarde.
              </div>
            )}

            <div className="mt-8 text-center">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-3 px-10 py-4 bg-[#1A8C7A] text-white font-bold text-lg rounded-full shadow-lg hover:bg-[#157a69] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Enviando...
                  </>
                ) : (
                  <>
                    CADASTRAR-ME AGORA
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
